import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/blog"); setPosts(data); } catch {} })(); }, []);

  return (
    <main className="pt-24 md:pt-32 pb-24 px-6 md:px-12 lg:px-16" data-testid="blog-list-page">
      <div className="mb-14">
        <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">The Journal</p>
        <h1 className="font-serif text-5xl md:text-6xl mt-3">Lookbook & Stories</h1>
        <p className="mt-5 max-w-xl text-[15px] text-[#4A4A4A] leading-relaxed">
          Slow reads on couture, craft, and the atelier — the way a magazine used to feel.
        </p>
      </div>

      {posts.length === 0 ? (
        <p className="text-[#7A7A7A] text-sm" data-testid="blog-empty">The first story is being embroidered. Check back soon.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-14">
          {posts.map((p, i) => (
            <Link
              key={p.post_id}
              to={`/blog/${p.slug}`}
              className={`group block ${i === 0 ? "md:col-span-2" : ""}`}
              data-testid={`blog-card-${p.slug}`}
            >
              <div className={`overflow-hidden bg-[#F3EFEA] ${i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
                <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out" />
              </div>
              <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mt-5">{p.tags?.join(" · ") || "Story"}</p>
              <h2 className={`font-serif mt-2 ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>{p.title}</h2>
              <p className="mt-3 text-[15px] text-[#4A4A4A] leading-relaxed max-w-xl">{p.excerpt}</p>
              <p className="mt-4 text-[11px] uppercase tracking-nav text-[#7A7A7A]">
                {p.author} · {p.published_at?.slice(0, 10)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { currency } = useCurrency();
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/blog/${slug}`); setPost(data); }
      catch { setNotFound(true); }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-40 text-center" data-testid="blog-notfound">
        <h1 className="font-serif text-4xl">Story not found.</h1>
        <Link to="/blog" className="mt-6 inline-block btn-editorial-ghost">Back to the Journal</Link>
      </main>
    );
  }
  if (!post) return <div className="pt-40 pb-40 text-center text-[#7A7A7A]">Loading…</div>;

  return (
    <main className="pt-24 md:pt-32 pb-24" data-testid="blog-post">
      <section className="px-6 md:px-16 max-w-4xl mx-auto">
        <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">{post.tags?.join(" · ") || "Story"}</p>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.05]">{post.title}</h1>
        <p className="mt-4 text-[11px] uppercase tracking-nav text-[#7A7A7A]">{post.author} · {post.published_at?.slice(0, 10)}</p>
      </section>
      <div className="mt-10">
        <img src={post.cover_image} alt="" className="w-full max-h-[70vh] object-cover" />
      </div>
      <article
        className="prose prose-lg max-w-3xl mx-auto px-6 md:px-0 pt-14 text-[#1A1A1A] font-serif text-[19px] leading-[1.7]"
        dangerouslySetInnerHTML={{ __html: post.body_html }}
        data-testid="blog-body"
      />

      {post.products && post.products.length > 0 && (
        <section className="mt-20 border-t border-[#BCA37F]/25 pt-14 px-6 md:px-12 lg:px-16" data-testid="blog-shop-the-story">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Shop the Story</p>
              <h3 className="font-serif text-3xl md:text-4xl mt-2">Pieces from this edit.</h3>
            </div>
            <Link
              to="/collections/festive"
              className="hidden md:inline text-[11px] uppercase tracking-nav hover:text-[#5C1A1B] transition-colors"
            >
              View the full atelier →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-10">
            {post.products.map((p) => (
              <Link
                key={p.product_id}
                to={`/product/${p.slug}`}
                className="product-card group"
                data-testid={`blog-shop-item-${p.slug}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFEA]">
                  <img
                    src={p.images?.[0]}
                    alt={p.name}
                    loading="lazy"
                    className="img-primary w-full h-full object-cover"
                  />
                  {p.images?.[1] && (
                    <img
                      src={p.images[1]}
                      alt=""
                      loading="lazy"
                      className="img-secondary w-full h-full object-cover"
                    />
                  )}
                </div>
                <p className="font-serif text-lg mt-3">{p.name}</p>
                <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">
                  {p.category}
                </p>
                <p className="text-sm text-[#1A1A1A] mt-2">
                  {formatPrice(p.price_inr, currency)}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
